import * as React from 'react'
import {
  ANCHOR_PATTERNS_BY_LABEL,
  GRAPH_NODE_TYPE_REGISTRY,
  NODE_PROPERTY_SCHEMAS,
  type NodeLabel
} from '@atbash-labs/ontology-core'
import {
  useGetPlantLabelCountsQuery,
  useGetPlantLabelEntitiesQuery,
  useGetPlantStrayCountsQuery,
  usePlantSessionPaint,
  type PlantEntityStrayFilter,
  type PlantEntityValidityFilter
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import { useChatAppContext } from '../../../chat-app-context'
import { buildModelBrowserAppContextEntries } from '../plant-app-context'
import { cn } from '../../../lib/utils'
import { PlantHref } from '../PlantHref'
import { registryIconFor } from '../registry-icons'
import { ConfidenceValue } from '../entity/ConfidenceValue'
import { formatTypedProperty } from '../entity/TypedPropertiesSection'
import {
  SessionDeletedBadge,
  SessionMarkAffordances,
  SessionOtherSectionBadge,
  SessionRollupPill,
  sessionMarkClass
} from '../session-mark-ui'

const ENTRIES = Object.values(GRAPH_NODE_TYPE_REGISTRY)
const GROUPS = Object.entries(
  ENTRIES.reduce<Record<string, typeof ENTRIES>>((groups, entry) => {
    const entries = groups[entry.domainGroup] ?? []
    entries.push(entry)
    groups[entry.domainGroup] = entries
    return groups
  }, {})
).sort(([a], [b]) => a.localeCompare(b))

function isStrayExempt(label: NodeLabel): boolean {
  return ANCHOR_PATTERNS_BY_LABEL[label].length === 0
}

/**
 * `/plant/model` — debug-only schema browser. Master–detail layout in
 * the same grammar as the PLC / Processes tabs: page header, left rail
 * styled like `PlantTreeNav`, and a table card on the right listing the
 * selected label's entities with search / validity / confidence filters.
 */
export default function ModelBrowserPage(): React.ReactElement {
  const [label, setLabel] = React.useState<NodeLabel>('Equipment')
  const [search, setSearch] = React.useState('')
  const [validity, setValidity] = React.useState<PlantEntityValidityFilter>('all')
  const [stray, setStray] = React.useState<PlantEntityStrayFilter>('all')
  const [minConfidence, setMinConfidence] = React.useState('')
  const [page, setPage] = React.useState(0)
  const pageSize = 50
  const registry = GRAPH_NODE_TYPE_REGISTRY[label]
  const strayExempt = isStrayExempt(label)
  const { markFor, markById } = usePlantSessionPaint()
  const { data: counts = [] } = useGetPlantLabelCountsQuery()
  const { data: strayCounts = [] } = useGetPlantStrayCountsQuery()
  const countByLabel = React.useMemo(
    () => new Map(counts.map((entry) => [entry.label, entry.total])),
    [counts]
  )
  const strayByLabel = React.useMemo(
    () => new Map(strayCounts.map((entry) => [entry.label, entry.total])),
    [strayCounts]
  )
  const addedByLabel = React.useMemo(() => {
    const byLabel = new Map<string, number>()
    for (const [id, entry] of markById) {
      if (markFor(id) !== 'added') continue
      for (const nodeLabel of entry.labels) {
        byLabel.set(nodeLabel, (byLabel.get(nodeLabel) ?? 0) + 1)
      }
    }
    return byLabel
  }, [markById, markFor])
  const modifiedByLabel = React.useMemo(() => {
    const byLabel = new Map<string, number>()
    for (const [id, entry] of markById) {
      if (markFor(id) !== 'touched') continue
      for (const nodeLabel of entry.labels) {
        byLabel.set(nodeLabel, (byLabel.get(nodeLabel) ?? 0) + 1)
      }
    }
    return byLabel
  }, [markById, markFor])
  const deletedByLabel = React.useMemo(() => {
    const byLabel = new Map<string, number>()
    for (const [id, entry] of markById) {
      if (markFor(id) !== 'deleted') continue
      for (const nodeLabel of entry.labels) {
        byLabel.set(nodeLabel, (byLabel.get(nodeLabel) ?? 0) + 1)
      }
    }
    return byLabel
  }, [markById, markFor])
  const labelGhosts = React.useMemo(() => {
    const ghosts: Array<{ id: string; name: string; view: 'deleted' | 'dimmed' }> = []
    for (const [id, entry] of markById) {
      if (entry.mark !== 'deleted') continue
      const view = markFor(id)
      if (view !== 'deleted' && view !== 'dimmed') continue
      if (!entry.labels.includes(label)) continue
      ghosts.push({ id, name: entry.name ?? id, view })
    }
    return ghosts
  }, [label, markById, markFor])
  const straySummary = React.useMemo(() => {
    let entities = 0
    let kinds = 0
    for (const entry of strayCounts) {
      if (entry.total > 0) {
        entities += entry.total
        kinds += 1
      }
    }
    return { entities, kinds }
  }, [strayCounts])

  const { data, isFetching } = useGetPlantLabelEntitiesQuery({
    label,
    search,
    validity,
    stray: strayExempt ? 'all' : stray,
    minConfidence: minConfidence === '' ? undefined : Number(minConfidence),
    page,
    pageSize
  })
  const strayIdSet = React.useMemo(() => new Set(data?.strayIds ?? []), [data?.strayIds])

  const chooseLabel = React.useCallback((next: NodeLabel, straysOnly = false) => {
    setLabel(next)
    setPage(0)
    if (straysOnly) setStray('stray')
  }, [])
  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / pageSize))

  useChatAppContext(
    buildModelBrowserAppContextEntries({
      label,
      total,
      search,
      validity,
      stray: strayExempt ? 'all' : stray,
      minConfidence
    })
  )

  return (
    <div className="flex flex-1 flex-col gap-card">
      <header>
        <h1 className="m-0 text-2xl font-semibold text-ink">Model</h1>
        <p className="mt-1 text-sm text-ink-3">
          {ENTRIES.length} entity kinds — every node label in the ontology schema, with the entities
          currently in the graph.
        </p>
        {straySummary.entities > 0 ? (
          <p className="mt-2 text-sm text-amber-800 dark:text-amber-300">
            Strays: {straySummary.entities} {straySummary.entities === 1 ? 'entity' : 'entities'}{' '}
            across {straySummary.kinds} {straySummary.kinds === 1 ? 'kind' : 'kinds'}.
          </p>
        ) : null}
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[17.5rem_minmax(0,1fr)]">
        <aside
          className="sticky top-4 self-start rounded-xl border bg-card p-2 text-card-foreground shadow-sm"
          aria-label="Model catalog"
        >
          <div className="px-2 pt-1 pb-2 text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">
            Model catalog
          </div>
          <div className="max-h-[calc(100vh-13.75rem)] overflow-y-auto">
            <ul className="m-0 list-none p-0">
              {GROUPS.map(([domain, entries]) => (
                <React.Fragment key={domain}>
                  <li className="flex items-center gap-2 px-2 py-1 text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
                    <span>{domain}</span>
                    <span className="font-mono text-10">{entries?.length ?? 0}</span>
                  </li>
                  {entries?.map((entry) => {
                    const Icon = registryIconFor(entry.label)
                    const selected = entry.label === label
                    const strayTotal = strayByLabel.get(entry.label) ?? 0
                    const addedTotal = addedByLabel.get(entry.label) ?? 0
                    const modifiedTotal = modifiedByLabel.get(entry.label) ?? 0
                    const deletedTotal = deletedByLabel.get(entry.label) ?? 0
                    return (
                      <li key={entry.label}>
                        <div
                          className={cn(
                            'flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-13',
                            selected ? 'bg-brand-soft font-semibold text-brand' : 'text-ink'
                          )}
                        >
                          <button
                            type="button"
                            onClick={() => chooseLabel(entry.label)}
                            aria-current={selected ? 'true' : undefined}
                            className={cn(
                              'flex min-w-0 flex-1 items-center gap-1.5 text-left transition-colors',
                              !selected && 'hover:text-accent-foreground'
                            )}
                          >
                            <Icon size={12} className="shrink-0 text-ink-3" aria-hidden />
                            <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                          </button>
                          <SessionRollupPill
                            added={addedTotal}
                            modified={modifiedTotal}
                            deleted={deletedTotal}
                          />
                          {strayTotal > 0 ? (
                            <button
                              type="button"
                              title={`Show ${strayTotal} stray${strayTotal === 1 ? '' : 's'}`}
                              onClick={() => chooseLabel(entry.label, true)}
                              className="rounded-full bg-amber-500/15 px-1.5 font-mono text-10 tabular-nums text-amber-800 hover:bg-amber-500/25 dark:text-amber-300"
                            >
                              {strayTotal}
                            </button>
                          ) : null}
                          <span className="font-mono text-10 tabular-nums text-ink-3">
                            {countByLabel.get(entry.label) ?? 0}
                          </span>
                        </div>
                      </li>
                    )
                  })}
                </React.Fragment>
              ))}
            </ul>
          </div>
        </aside>

        <section className="flex min-w-0 flex-col overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
          <div className="border-b px-4 py-3">
            <div className="flex flex-wrap items-baseline gap-2">
              <h2 className="m-0 text-lg font-semibold text-ink">{label}</h2>
              <Badge variant="outline">{registry.domainGroup}</Badge>
              <span className="text-xs tabular-nums text-ink-3">
                {total} {total === 1 ? 'entity' : 'entities'}
              </span>
              <SessionRollupPill
                added={addedByLabel.get(label) ?? 0}
                modified={modifiedByLabel.get(label) ?? 0}
                deleted={deletedByLabel.get(label) ?? 0}
              />
              {!strayExempt && (strayByLabel.get(label) ?? 0) > 0 ? (
                <span className="text-xs tabular-nums text-amber-800 dark:text-amber-300">
                  {strayByLabel.get(label)} stray
                  {(strayByLabel.get(label) ?? 0) === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-[minmax(12.5rem,1fr)_9.375rem_9.375rem_9.375rem]">
              <Input
                className="h-8"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value)
                  setPage(0)
                }}
                placeholder={`Search ${registry.searchableProperties.join(', ')}`}
              />
              <select
                value={validity}
                onChange={(event) => {
                  setValidity(event.target.value as PlantEntityValidityFilter)
                  setPage(0)
                }}
                className="h-8 rounded-md border bg-background px-2 text-sm"
              >
                <option value="all">All validity</option>
                <option value="trusted">Trusted</option>
                <option value="invalidated">Invalidated</option>
              </select>
              <select
                value={strayExempt ? 'all' : stray}
                disabled={strayExempt}
                title={strayExempt ? 'This label is stray-exempt (no anchor patterns)' : undefined}
                onChange={(event) => {
                  setStray(event.target.value as PlantEntityStrayFilter)
                  setPage(0)
                }}
                className="h-8 rounded-md border bg-background px-2 text-sm disabled:opacity-60"
              >
                <option value="all">All</option>
                <option value="stray">Strays only</option>
              </select>
              <Input
                className="h-8"
                type="number"
                min="0"
                max="1"
                step="0.1"
                value={minConfidence}
                onChange={(event) => {
                  setMinConfidence(event.target.value)
                  setPage(0)
                }}
                placeholder="Min confidence"
              />
            </div>
          </div>

          <div className="overflow-hidden">
            <table className="w-full table-fixed text-left text-13">
              <thead>
                <tr className="border-b text-10 uppercase tracking-[0.04em] text-ink-3">
                  {registry.listColumns.map((column) => (
                    <th key={column} title={column} className="truncate px-3 py-2 font-semibold">
                      {column}
                    </th>
                  ))}
                  <th className="w-32 px-3 py-2 text-right font-semibold">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {data?.entities.map((entity) => {
                  const isStray = strayIdSet.has(entity.id)
                  const mark = markFor(entity.id)
                  return (
                    <tr
                      key={entity.id}
                      className={sessionMarkClass(
                        cn(
                          'border-b transition-colors last:border-b-0',
                          isStray && mark !== 'added'
                            ? 'bg-amber-500/10 hover:bg-amber-500/15'
                            : !mark && 'hover:bg-accent/50'
                        ),
                        mark
                      )}
                    >
                      {registry.listColumns.map((column, index) => {
                        const schema = NODE_PROPERTY_SCHEMAS[label].properties[column]
                        const value = schema
                          ? formatTypedProperty(entity[column], schema).join(', ')
                          : String(entity[column] ?? '')
                        return (
                          <td
                            key={column}
                            className={cn(
                              'min-w-0 px-3 py-2 align-top',
                              schema?.type === 'number' && 'font-mono tabular-nums text-ink-2'
                            )}
                          >
                            {index === 0 ? (
                              <span className="flex min-w-0 items-center gap-1.5">
                                <PlantHref
                                  id={entity.id}
                                  label={label}
                                  node={entity}
                                  title={value || entity.name}
                                  className="min-w-0 flex-1 truncate font-medium text-brand hover:underline"
                                >
                                  {value || entity.name}
                                </PlantHref>
                                <SessionMarkAffordances mark={mark} />
                                {isStray ? (
                                  <span className="shrink-0 rounded-full border border-amber-400/40 bg-amber-500/10 px-1.5 py-0.5 text-10 font-medium text-amber-800 dark:text-amber-300">
                                    Stray
                                  </span>
                                ) : null}
                              </span>
                            ) : (
                              <span title={value} className="block truncate text-ink-2">
                                {value}
                              </span>
                            )}
                          </td>
                        )
                      })}
                      <td className="w-32 whitespace-nowrap px-3 py-2 text-right align-top">
                        {entity.confidence === undefined ? (
                          <span className="font-mono text-11 text-ink-4">—</span>
                        ) : (
                          <ConfidenceValue
                            value={entity.confidence}
                            compact
                            className="whitespace-nowrap"
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
                {labelGhosts.map((ghost) => (
                  <tr
                    key={`ghost:${ghost.id}`}
                    className={cn(
                      'border-b last:border-b-0',
                      ghost.view === 'dimmed'
                        ? 'border-line bg-bg-sunk/70'
                        : 'border-st-down/30 bg-st-down-bg'
                    )}
                    title={
                      ghost.view === 'dimmed'
                        ? 'changed by another section'
                        : 'deleted this session'
                    }
                  >
                    <td colSpan={registry.listColumns.length + 1} className="px-3 py-2 align-top">
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate text-13 font-medium line-through',
                            ghost.view === 'dimmed' ? 'text-ink-4' : 'text-st-down'
                          )}
                        >
                          {ghost.name}
                        </span>
                        {ghost.view === 'dimmed' ? (
                          <SessionOtherSectionBadge />
                        ) : (
                          <SessionDeletedBadge />
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
                {!isFetching && (data?.entities.length ?? 0) === 0 && labelGhosts.length === 0 && (
                  <tr>
                    <td
                      colSpan={registry.listColumns.length + 1}
                      className="h-28 px-4 text-center text-sm text-ink-3"
                    >
                      No matching entities
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-auto flex items-center justify-between border-t px-4 py-2.5">
            <span className="text-xs tabular-nums text-ink-3">
              Page {page + 1} of {pages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
